import { AppThunkAction } from '../reducers';

// services
import * as Services from '../../services/PersonAddresses.Services';

// actions
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonAddresses.Actions';

// models
import { ErrorType } from '../../models/HttpError';
import { AddressViewModel, CreatePersonAddressCommand, UpdatePersonAddressCommand } from '../../models/Entities/Person/Address/AddressModels';

export type KnownAction =
    | Actions.PersonAddresses.PersonAddressGetAllRequestAction
    | Actions.PersonAddresses.PersonAddressGetAllSuccessAction
    | Actions.PersonAddresses.PersonAddressGetAllFailureAction
    | Actions.PersonAddresses.PersonAddressGetByIdRequestAction
    | Actions.PersonAddresses.PersonAddressGetByIdSuccessAction
    | Actions.PersonAddresses.PersonAddressGetByIdFailureAction
    | Actions.PersonAddresses.PersonAddressCreateRequestAction
    | Actions.PersonAddresses.PersonAddressCreateSuccessAction
    | Actions.PersonAddresses.PersonAddressCreateFailureAction
    | Actions.PersonAddresses.PersonAddressUpdateRequestAction
    | Actions.PersonAddresses.PersonAddressUpdateSuccessAction
    | Actions.PersonAddresses.PersonAddressUpdateFailureAction
    | Actions.PersonAddresses.PersonAddressDeleteRequestAction
    | Actions.PersonAddresses.PersonAddressDeleteSuccessAction
    | Actions.PersonAddresses.PersonAddressDeleteFailureAction;

const NotAuthorizedError: ErrorType = {
    ErrorCode: 401,
    ErrorMessage: 'Not authorized',
    Errors: []
};

export const actionCreators = {
    GetAddressesByPersonId: (personId: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_ADDRESS_GETALL_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_ADDRESS_GETALL_REQUEST, personId });

        Services.GetAddressesByPersonId(token, personId)
            .then((response: AddressViewModel[]) => {
                dispatch({
                    type: ActionEvents.PERSON_ADDRESS_GETALL_SUCCESS,
                    personId,
                    payload: response.map((item: AddressViewModel) => ({ ...item, personId }))
                });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_ADDRESS_GETALL_FAILURE, error });
            });
    },

    GetAddressById: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_ADDRESS_GETBYID_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_ADDRESS_GETBYID_REQUEST, id });

        Services.GetAddressById(token, id)
            .then((response: AddressViewModel) => {
                dispatch({ type: ActionEvents.PERSON_ADDRESS_GETBYID_SUCCESS, payload: response });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_ADDRESS_GETBYID_FAILURE, error });
            });
    },

    CreateAddress: (payload: CreatePersonAddressCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_ADDRESS_CREATE_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_ADDRESS_CREATE_REQUEST, payload });

        Services.CreateAddress(token, payload)
            .then((created: AddressViewModel) => {
                dispatch({ type: ActionEvents.PERSON_ADDRESS_CREATE_SUCCESS, payload: { ...created, personId: payload.personId } });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_ADDRESS_CREATE_FAILURE, error });
            });
    },

    UpdateAddress: (payload: UpdatePersonAddressCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_ADDRESS_UPDATE_FAILURE, id: payload.id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_ADDRESS_UPDATE_REQUEST, payload });

        Services.UpdateAddress(token, payload)
            .then((updated: AddressViewModel) => {
                dispatch({ type: ActionEvents.PERSON_ADDRESS_UPDATE_SUCCESS, payload: { ...updated, personId: payload.personId } });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_ADDRESS_UPDATE_FAILURE, id: payload.id, error });
            });
    },

    DeleteAddress: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_ADDRESS_DELETE_FAILURE, id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_ADDRESS_DELETE_REQUEST, id });

        Services.DeleteAddress(token, id)
            .then(() => {
                dispatch({ type: ActionEvents.PERSON_ADDRESS_DELETE_SUCCESS, id });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_ADDRESS_DELETE_FAILURE, id, error });
            });
    }
};
